import dayjs from 'dayjs';
import React from 'react';
import { toast } from '~/utils/toast';
import { StatusOptions } from '~/config';
import { api } from '~/utils/api';

import { CheckIcon } from '@heroicons/react/24/outline';

import Button from '../Button';
import Modal, { useModal } from '../Modal';
import TextInput from '../TextInput';
import Tooltip from '../Tooltip';

type ConfirmTrademarkModalProps = {
  tmInstanceId: string;
  reloadDoc: () => Promise<void>;
};

export default function ConfirmTrademarkModal({
  tmInstanceId,
  reloadDoc,
}: ConfirmTrademarkModalProps) {
  const { modalRef, openModal, closeModal } = useModal();

  const [approval, setApproval] = React.useState<
    'LIMITED_APPROVAL' | 'STANDING_APPROVAL' | 'PROPRIETARY' | null
  >(null);
  const [limitations, setLimitations] = React.useState<string>('');
  const [expirationDate, setExpirationDate] = React.useState<string | null>(null);
  const { mutateAsync: actionPolicyFeedback } = api.feedback.actionPolicyFeedback.useMutation();
  const [isLoading, setIsLoading] = React.useState(false);

  return (
    <>
      <Tooltip content="Confirm" hideIcon>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Confirm trademark"
          onClick={(e) => {
            e.stopPropagation();
            openModal();
          }}
        >
          <CheckIcon className="size-4" strokeWidth={2} />
        </Button>
      </Tooltip>
      <Modal ref={modalRef} header={'Confirm Permission'}>
        <div className="mt-3 text-sm">
          <label className="flex cursor-pointer items-center gap-2 py-1.5">
            <input
              type="radio"
              name={`confirm-trademark-${tmInstanceId}`}
              className="border-input size-3.5 shrink-0 accent-foreground"
              onChange={(e) => {
                setApproval(e.target.checked ? 'PROPRIETARY' : null);
              }}
            />
            <span className="text-sm font-medium">Proprietary</span>
          </label>
          <label className="flex cursor-pointer items-center gap-2 py-1.5">
            <input
              type="radio"
              name={`confirm-trademark-${tmInstanceId}`}
              className="border-input size-3.5 shrink-0 accent-foreground"
              onChange={(e) => {
                setApproval(e.target.checked ? 'LIMITED_APPROVAL' : null);
              }}
            />
            <span className="text-sm font-medium">Limited Approval</span>
          </label>
          {approval === 'LIMITED_APPROVAL' && (
            <>
              <TextInput
                inputProps={{ role: 'button', tabIndex: 0 }}
                placeholder="Enter limitations"
                label=""
                className="mb-2"
                value={limitations}
                onChange={(e) => {
                  setLimitations(e.target.value);
                }}
              />
              <TextInput
                inputProps={{ type: 'date', min: dayjs().format('YYYY-MM-DD') }}
                onChange={(e) => {
                  setExpirationDate(e.target.value);
                }}
                label="Expiration Date of Approval"
              />
            </>
          )}
          <label className="flex cursor-pointer items-center gap-2 py-1.5">
            <input
              type="radio"
              name={`confirm-trademark-${tmInstanceId}`}
              className="border-input size-3.5 shrink-0 accent-foreground"
              checked={approval === 'STANDING_APPROVAL'}
              onChange={(e) => {
                setApproval(e.target.checked ? 'STANDING_APPROVAL' : null);
              }}
            />
            <span className="text-sm font-medium">Standing Approval</span>
          </label>

          <Button
            type="button"
            label="Submit"
            size="sm"
            className="mt-3"
            fullWidth
            onClick={async () => {
              if (!approval) return;
              try {
                setIsLoading(true);
                await actionPolicyFeedback({
                  trademarkInstance: {
                    id: tmInstanceId,
                    approvalType: approval === 'PROPRIETARY' ? null : approval,
                    status: StatusOptions.APPROVED,
                    approvalLimitations: approval === 'LIMITED_APPROVAL' ? limitations : '',
                    approvalExpireAt: expirationDate
                      ? new Date(expirationDate).toISOString()
                      : undefined,
                  },
                });
                await reloadDoc();
                toast.success('Trademark has been approved');
                closeModal();
              } catch (e) {
                const msg = 'Failed to approve trademark';
                console.error(msg, e);
                toast.error(msg);
              } finally {
                setIsLoading(false);
              }
            }}
            isLoading={isLoading}
            disabled={!approval}
          />
        </div>
      </Modal>
    </>
  );
}
